/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmTerritoryOptions.ts
 * PURPOSE: CRM shell select options from operations territories; fallback to mock IDs when API empty
 * DEPENDENCIES: @tanstack/react-query, ../../../hooks/territoryFetch, ../data/opportunityDrawerOptions
 * PUBLIC_API: useCrmTerritoryOptions, CRM_TERRITORY_OPTIONS_QUERY_KEY, buildTerritoryAccountCounts
 * SIDE_EFFECTS: GET /api/v1/territories via React Query cache
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95/150
 * RECENT: 2026-05-25 — territory integration territory integration (wire ops territories into CRM shell selectors).
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchAllTerritoriesFlat } from '../../../hooks/territoryFetch';
import { OPPORTUNITY_TERRITORY_OPTIONS } from '../data/opportunityDrawerOptions';

export const CRM_TERRITORY_OPTIONS_QUERY_KEY = ['crm', 'territory-options'] as const;

export type TerritorySelectOption = { value: string; label: string };

export function buildTerritoryAccountCounts(
  accounts: Array<{ territory_id?: string | null; territoryId?: string | null }>
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of accounts) {
    const tid = String(row.territoryId ?? row.territory_id ?? '').trim();
    if (!tid) continue;
    counts.set(tid, (counts.get(tid) ?? 0) + 1);
  }
  return counts;
}

export function resolveTerritoryLabel(
  id: string | null | undefined,
  options: TerritorySelectOption[],
  territories: Array<{ id: string; name: string; territory_code?: string }> = []
): string {
  const key = String(id ?? '').trim();
  if (!key) return 'Unassigned';
  const fromOptions = options.find((o) => o.value === key);
  if (fromOptions) return fromOptions.label;
  const mock = OPPORTUNITY_TERRITORY_OPTIONS.find((o) => o.value === key);
  if (mock) return mock.label;
  const row = territories.find((t) => t.id === key);
  if (row) return row.territory_code ? `${row.name} (${row.territory_code})` : row.name;
  return key;
}

export function useCrmTerritoryOptions() {
  const query = useQuery({
    queryKey: CRM_TERRITORY_OPTIONS_QUERY_KEY,
    queryFn: () => fetchAllTerritoriesFlat(false),
    staleTime: 5 * 60 * 1000,
  });

  const activeTerritories = useMemo(
    () => (query.data ?? []).filter((t) => t.is_active),
    [query.data]
  );

  const options: TerritorySelectOption[] = useMemo(() => {
    if (activeTerritories.length === 0) {
      return OPPORTUNITY_TERRITORY_OPTIONS;
    }
    return [
      { value: '', label: 'Unassigned' },
      ...activeTerritories.map((t) => ({
        value: t.id,
        label: t.territory_code ? `${t.name} (${t.territory_code})` : t.name,
      })),
    ];
  }, [activeTerritories]);

  const territoryLabel = useCallback(
    (id: string | null | undefined): string =>
      resolveTerritoryLabel(id, options, query.data ?? []),
    [options, query.data]
  );

  return {
    options,
    territoryLabel,
    territories: query.data ?? [],
    activeTerritories,
    loading: query.isLoading,
    isLive: activeTerritories.length > 0,
    error: query.error,
  };
}
